"use client";
import React, { useEffect } from "react";
import { store } from "store";
import Providers from "../providers";
import { setLocale } from "features/config/config.reducer";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "video.js/dist/video-js.css";

import { useAppSelector } from "appHooks";
import { configSelector } from "features/config/config.selectors";
import { clientSelector } from "features/client/client.selectors";
import { resolveKey } from "utils/getTranslation";
import { PAGE_TITLE_KEYS } from "constants/pageTitles";

import { usePathname } from "next/navigation";

const formatName = (name: string) =>
  name
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const DynamicTitle = () => {
  const { slug, locale } = useAppSelector(configSelector);
  const { data } = useAppSelector(clientSelector);
  const pathname = usePathname();

  useEffect(() => {
    if (!slug) return;

    const localizedName = locale === "ar" ? data.name_ar : data.name;
    const clientName = localizedName || formatName(slug);

    const segments = pathname.split("/").filter(Boolean);
    const pageSegment = segments[1];
    const titleKey = pageSegment ? PAGE_TITLE_KEYS[pageSegment] : undefined;
    const pageTitle = titleKey ? resolveKey(locale || "en", titleKey) : "";

    document.title = pageTitle ? `${pageTitle} | ${clientName}` : clientName;
  }, [slug, pathname, locale, data.name, data.name_ar]);

  return null;
};

const ClientLayout: React.FC<{
  children: React.ReactNode;
  params: {
    locale: string;
  };
}> = ({ children, params }) => {
  const { locale } = store.getState().config;

  useEffect(() => {
    if (locale !== params.locale) {
      store.dispatch(setLocale(params.locale));
    }
  }, [params.locale]);

  return (
    <>
      <Providers locale={params.locale}>
        <DynamicTitle />
        {children}
      </Providers>
      <ToastContainer />
    </>
  );
};

export default ClientLayout;
